import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { payeesQuery, terminalsQuery } from '@/api/cash'
import { branchesQuery } from '@/api/queries'
import { PayeesSection } from '@/app/cash/payees-section'
import { TerminalsCard } from '@/app/cash/terminals-card'
import { WeekCard } from '@/app/cash/week-card'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { SettingsTabs } from '@/app/settings-tabs'
import {
  canManageCashSettings,
  canManageClubCash,
  canManagePayees,
  canSeeCashSettings,
  cashClubs,
  cashErrorText,
} from '@/domain/cash'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { InlineBanner } from '@/ui/inline-banner'
import { Page, PageHeader } from '@/ui/page'
import { Select } from '@/ui/select'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/settings/cash')({
  component: CashSettings,
})

/// «Налаштування → Каса» (specs/cash-desk.md §3.1–§3.3, §17): the company's
/// ФОП, then — for one club at a time — its week of payees and its bank
/// terminals. Read by finance (`finance ≥ view`, the РНОКПП and IBAN
/// included), changed by `finance = manage`: payees at company scope, a
/// club's week and terminals within the writer's clubs. The server keeps
/// every rule; anyone else is not shown the tab.
function CashSettings() {
  const { orgId } = Route.useParams()
  const { access, caller } = useCompanyCaller(orgId)
  const allowed = canSeeCashSettings(caller)
  const payees = useQuery({ ...payeesQuery(orgId), enabled: allowed })
  const terminals = useQuery({ ...terminalsQuery(orgId), enabled: allowed })
  const branches = useQuery({ ...branchesQuery(orgId), enabled: allowed })
  const [pickedClub, setPickedClub] = useState<string | null>(null)
  const [news, setNews] = useState<string | null>(null)

  if (!allowed) return <NothingForRole />

  const failed = payees.error ?? terminals.error ?? branches.error
  const clubs = cashClubs(branches.data ?? [], access)
  const club = clubs.find((branch) => branch.id === pickedClub) ?? clubs[0] ?? null
  const allBranches = branches.data ?? []

  return (
    <Page>
      <PageHeader title={t.settings.title} subtitle={t.cash.settings.subtitle} tabs={<SettingsTabs orgId={orgId} caller={caller} />} />

      {failed ? (
        <InlineBanner
          kind="error"
          message={errorText(failed, cashErrorText(allBranches))}
          action={{
            label: t.app.retry,
            onClick: () => {
              void payees.refetch()
              void terminals.refetch()
              void branches.refetch()
            },
          }}
        />
      ) : !payees.data || !terminals.data || !branches.data ? (
        <LoadingBlock label={t.app.loading} />
      ) : (
        <div className="flex max-w-[640px] flex-col gap-8">
          {news ? <InlineBanner kind="done" message={news} /> : null}
          {canManageCashSettings(caller) ? null : <InlineBanner kind="info" message={t.cash.settings.readOnly} />}

          <PayeesSection
            orgId={orgId}
            payees={payees.data}
            branches={allBranches}
            manage={canManagePayees(caller)}
            onNews={setNews}
          />

          {club ? (
            <div className="flex flex-col gap-8">
              {clubs.length > 1 ? (
                <div className="flex items-center gap-3">
                  <span className="text-caption font-medium text-text-2">{t.cash.settings.club}</span>
                  <Select
                    variant="toolbar"
                    ariaLabel={t.cash.settings.club}
                    value={club.id}
                    options={clubs.map((branch) => ({ value: branch.id, label: branch.name }))}
                    onChange={(branchId) => {
                      setNews(null)
                      setPickedClub(branchId)
                    }}
                  />
                </div>
              ) : null}
              <WeekCard
                key={`week-${club.id}`}
                orgId={orgId}
                club={club}
                payees={payees.data}
                branches={allBranches}
                manage={canManageClubCash(caller, access, club.id)}
              />
              <TerminalsCard
                orgId={orgId}
                club={club}
                terminals={terminals.data}
                payees={payees.data}
                branches={allBranches}
                manage={canManageClubCash(caller, access, club.id)}
                onNews={setNews}
              />
            </div>
          ) : null}
        </div>
      )}
    </Page>
  )
}
